import type { CareerField } from './schema';

/**
 * Twelve career fields. Each is a different *shape* of working life, not just a
 * different salary number: trades pay early and plateau, research pays late and
 * pays in meaning, sales is volatile and reputation-driven, care work is
 * high-meaning and low-ceiling. Balance work tunes these shapes, so the
 * dominant strategy question stays open.
 */
const FIELDS: CareerField[] = [
  {
    id: 'software',
    label: 'Software',
    blurb: 'High ceiling, high pace, and a skills treadmill that never quite stops.',
    skillGrowth: { technical: 4.2, communication: 1.1, management: 0.5, business: 0.5 },
    entry: { skills: { technical: 22 } },
    roles: [
      { level: 0, title: 'Junior Engineer', baseSalary: 62000, workload: 52, requires: { technical: 22 } },
      { level: 1, title: 'Engineer', baseSalary: 92000, workload: 58, requires: { technical: 38 }, minExperience: 2 },
      { level: 2, title: 'Senior Engineer', baseSalary: 134000, workload: 65, requires: { technical: 56, communication: 34 }, minExperience: 5 },
      { level: 3, title: 'Staff Engineer', baseSalary: 178000, workload: 70, requires: { technical: 72, communication: 48 }, minExperience: 9 },
      { level: 4, title: 'Principal / Director', baseSalary: 236000, workload: 76, requires: { technical: 80, management: 58, communication: 62 }, minExperience: 14 },
    ],
    stability: 62,
    meaning: 52,
    reputationLeverage: 0.34,
    pools: ['pool.career.knowledge', 'pool.career.tech'],
    tags: ['knowledge', 'high-income', 'fast'],
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    blurb: 'Deeply meaningful, chronically understaffed, and physically relentless.',
    skillGrowth: { technical: 2.6, social: 2.4, selfManagement: 1.4, communication: 1.6 },
    entry: { education: 'vocational', skills: { technical: 18, social: 24 } },
    roles: [
      { level: 0, title: 'Care Assistant', baseSalary: 41000, workload: 62, requires: { social: 24 } },
      { level: 1, title: 'Registered Practitioner', baseSalary: 68000, workload: 70, requires: { technical: 36, social: 40 }, minExperience: 3 },
      { level: 2, title: 'Specialist', baseSalary: 98000, workload: 76, requires: { technical: 54, social: 52 }, minExperience: 7 },
      { level: 3, title: 'Lead Clinician', baseSalary: 138000, workload: 80, requires: { technical: 70, management: 46 }, minExperience: 12 },
      { level: 4, title: 'Department Head', baseSalary: 182000, workload: 82, requires: { management: 64, communication: 60 }, minExperience: 18 },
    ],
    stability: 84,
    meaning: 82,
    reputationLeverage: 0.2,
    pools: ['pool.career.care', 'pool.career.shift'],
    tags: ['care', 'stable', 'demanding'],
  },
  {
    id: 'education',
    label: 'Education',
    blurb: 'A long game. The pay curve is flat; the human return is not.',
    skillGrowth: { communication: 3.2, social: 2.2, creative: 1.2, management: 0.8 },
    entry: { education: 'bachelor', skills: { communication: 26 } },
    roles: [
      { level: 0, title: 'Teaching Assistant', baseSalary: 36000, workload: 55, requires: { communication: 26 } },
      { level: 1, title: 'Teacher', baseSalary: 54000, workload: 64, requires: { communication: 40 }, minExperience: 2 },
      { level: 2, title: 'Senior Teacher', baseSalary: 71000, workload: 66, requires: { communication: 55, management: 30 }, minExperience: 7 },
      { level: 3, title: 'Head of Department', baseSalary: 92000, workload: 70, requires: { management: 50, communication: 65 }, minExperience: 12 },
      { level: 4, title: 'Principal', baseSalary: 121000, workload: 74, requires: { management: 68, communication: 72 }, minExperience: 18 },
    ],
    stability: 86,
    meaning: 84,
    reputationLeverage: 0.18,
    pools: ['pool.career.care', 'pool.career.institution'],
    tags: ['care', 'stable', 'meaning'],
  },
  {
    id: 'finance',
    label: 'Finance',
    blurb: 'Money is the product. So are the hours.',
    skillGrowth: { financial: 4, business: 2.4, communication: 1.2 },
    entry: { education: 'bachelor', skills: { financial: 24 } },
    roles: [
      { level: 0, title: 'Analyst', baseSalary: 74000, workload: 70, requires: { financial: 24 } },
      { level: 1, title: 'Associate', baseSalary: 112000, workload: 78, requires: { financial: 40, business: 32 }, minExperience: 3 },
      { level: 2, title: 'Vice President', baseSalary: 168000, workload: 84, requires: { financial: 58, business: 50 }, minExperience: 7 },
      { level: 3, title: 'Director', baseSalary: 245000, workload: 86, requires: { financial: 72, management: 58 }, minExperience: 12 },
      { level: 4, title: 'Partner', baseSalary: 372000, workload: 88, requires: { financial: 82, management: 70, communication: 68 }, minExperience: 17 },
    ],
    stability: 54,
    meaning: 36,
    reputationLeverage: 0.42,
    pools: ['pool.career.knowledge', 'pool.career.corporate'],
    tags: ['high-income', 'demanding', 'volatile'],
  },
  {
    id: 'creative',
    label: 'Creative & Design',
    blurb: 'Freedom and precarity, wrapped in the same contract.',
    skillGrowth: { creative: 4, communication: 1.6, business: 1, technical: 0.8 },
    entry: { skills: { creative: 26 } },
    roles: [
      { level: 0, title: 'Junior Designer', baseSalary: 44000, workload: 52, requires: { creative: 26 } },
      { level: 1, title: 'Designer', baseSalary: 68000, workload: 56, requires: { creative: 42, communication: 30 }, minExperience: 2 },
      { level: 2, title: 'Senior Designer', baseSalary: 96000, workload: 62, requires: { creative: 60, communication: 44 }, minExperience: 6 },
      { level: 3, title: 'Design Lead', baseSalary: 128000, workload: 68, requires: { creative: 72, management: 46 }, minExperience: 10 },
      { level: 4, title: 'Creative Director', baseSalary: 168000, workload: 72, requires: { creative: 82, management: 60, communication: 64 }, minExperience: 15 },
    ],
    stability: 48,
    meaning: 72,
    reputationLeverage: 0.48,
    pools: ['pool.career.creative', 'pool.career.freelance'],
    tags: ['creative', 'volatile', 'meaning'],
  },
  {
    id: 'trades',
    label: 'Skilled Trades',
    blurb: 'Earns early, needs no degree, and asks your body for the rent.',
    skillGrowth: { technical: 3.4, business: 1.4, selfManagement: 1.2, social: 0.8 },
    entry: { skills: { technical: 16 } },
    roles: [
      { level: 0, title: 'Apprentice', baseSalary: 34000, workload: 62, requires: {} },
      { level: 1, title: 'Tradesperson', baseSalary: 62000, workload: 68, requires: { technical: 34 }, minExperience: 3 },
      { level: 2, title: 'Senior Tradesperson', baseSalary: 84000, workload: 70, requires: { technical: 52 }, minExperience: 7 },
      { level: 3, title: 'Site Supervisor', baseSalary: 106000, workload: 72, requires: { technical: 62, management: 42 }, minExperience: 12 },
      { level: 4, title: 'Contractor / Owner', baseSalary: 142000, workload: 78, requires: { business: 54, management: 56 }, minExperience: 16 },
    ],
    stability: 70,
    meaning: 62,
    reputationLeverage: 0.38,
    pools: ['pool.career.physical', 'pool.career.trade'],
    tags: ['physical', 'early-earning', 'no-degree'],
  },
  {
    id: 'sales',
    label: 'Sales & Growth',
    blurb: 'Uncapped upside, unflinching quota, and a scoreboard that resets monthly.',
    skillGrowth: { communication: 3.6, social: 2.6, business: 2 },
    entry: { skills: { communication: 22, social: 22 } },
    roles: [
      { level: 0, title: 'Sales Associate', baseSalary: 46000, workload: 58, requires: { communication: 22 } },
      { level: 1, title: 'Account Executive', baseSalary: 82000, workload: 66, requires: { communication: 40, social: 38 }, minExperience: 2 },
      { level: 2, title: 'Senior AE', baseSalary: 124000, workload: 72, requires: { communication: 56, business: 42 }, minExperience: 5 },
      { level: 3, title: 'Sales Manager', baseSalary: 164000, workload: 78, requires: { management: 52, communication: 66 }, minExperience: 9 },
      { level: 4, title: 'VP Revenue', baseSalary: 228000, workload: 82, requires: { management: 68, business: 66, communication: 74 }, minExperience: 14 },
    ],
    stability: 46,
    meaning: 42,
    reputationLeverage: 0.5,
    pools: ['pool.career.corporate', 'pool.career.commission'],
    tags: ['volatile', 'social', 'high-income'],
  },
  {
    id: 'law',
    label: 'Law & Policy',
    blurb: 'Credential-heavy, prestige-rich, and measured in billable hours.',
    skillGrowth: { communication: 3, business: 1.8, social: 1.2, technical: 0.6 },
    entry: { education: 'master', credential: 'cred.law', skills: { communication: 34 } },
    roles: [
      { level: 0, title: 'Paralegal', baseSalary: 52000, workload: 64, requires: { communication: 28 } },
      { level: 1, title: 'Associate', baseSalary: 96000, workload: 78, requires: { communication: 44 }, minExperience: 2 },
      { level: 2, title: 'Senior Associate', baseSalary: 148000, workload: 84, requires: { communication: 60, business: 40 }, minExperience: 6 },
      { level: 3, title: 'Counsel', baseSalary: 196000, workload: 82, requires: { communication: 72, management: 48 }, minExperience: 11 },
      { level: 4, title: 'Partner', baseSalary: 288000, workload: 86, requires: { communication: 82, business: 66, management: 64 }, minExperience: 16 },
    ],
    stability: 64,
    meaning: 52,
    reputationLeverage: 0.44,
    pools: ['pool.career.knowledge', 'pool.career.institution'],
    tags: ['credential', 'demanding', 'high-income'],
  },
  {
    id: 'research',
    label: 'Science & Research',
    blurb: 'Curiosity as a career. The grants are finite; the questions are not.',
    skillGrowth: { technical: 3.2, creative: 1.8, communication: 1.4, selfManagement: 1 },
    entry: { education: 'master', skills: { technical: 34 } },
    roles: [
      { level: 0, title: 'Research Assistant', baseSalary: 42000, workload: 60, requires: { technical: 30 } },
      { level: 1, title: 'Researcher', baseSalary: 64000, workload: 64, requires: { technical: 46 }, minExperience: 3 },
      { level: 2, title: 'Senior Researcher', baseSalary: 89000, workload: 66, requires: { technical: 62, communication: 42 }, minExperience: 8 },
      { level: 3, title: 'Principal Investigator', baseSalary: 124000, workload: 72, requires: { technical: 76, management: 46, communication: 58 }, minExperience: 13 },
      { level: 4, title: 'Institute Lead', baseSalary: 162000, workload: 74, requires: { technical: 82, management: 62, communication: 68 }, minExperience: 19 },
    ],
    stability: 56,
    meaning: 86,
    reputationLeverage: 0.4,
    pools: ['pool.career.knowledge', 'pool.career.institution'],
    tags: ['knowledge', 'meaning', 'slow-burn'],
  },
  {
    id: 'public',
    label: 'Public Service',
    blurb: 'Security, pension, and the slow grind of institutional gravity.',
    skillGrowth: { communication: 2.2, management: 2, social: 1.6, financial: 0.8 },
    entry: { skills: { communication: 20 } },
    roles: [
      { level: 0, title: 'Officer', baseSalary: 43000, workload: 48, requires: {} },
      { level: 1, title: 'Senior Officer', baseSalary: 62000, workload: 52, requires: { communication: 36 }, minExperience: 3 },
      { level: 2, title: 'Manager', baseSalary: 82000, workload: 58, requires: { management: 42, communication: 48 }, minExperience: 8 },
      { level: 3, title: 'Senior Manager', baseSalary: 106000, workload: 62, requires: { management: 58, communication: 58 }, minExperience: 13 },
      { level: 4, title: 'Director', baseSalary: 138000, workload: 66, requires: { management: 70, communication: 68 }, minExperience: 19 },
    ],
    stability: 92,
    meaning: 66,
    reputationLeverage: 0.16,
    pools: ['pool.career.institution', 'pool.career.stable'],
    tags: ['stable', 'pension', 'slow'],
  },
  {
    id: 'hospitality',
    label: 'Hospitality & Service',
    blurb: 'Enter tomorrow, learn fast, and feel every economic cycle first.',
    skillGrowth: { social: 3, communication: 2.2, business: 1.6, selfManagement: 1 },
    entry: { skills: {} },
    roles: [
      { level: 0, title: 'Service Crew', baseSalary: 28000, workload: 58, requires: {} },
      { level: 1, title: 'Shift Lead', baseSalary: 40000, workload: 64, requires: { social: 30 }, minExperience: 2 },
      { level: 2, title: 'Assistant Manager', baseSalary: 56000, workload: 70, requires: { management: 34, social: 44 }, minExperience: 5 },
      { level: 3, title: 'General Manager', baseSalary: 82000, workload: 76, requires: { management: 52, business: 44 }, minExperience: 9 },
      { level: 4, title: 'Regional Manager', baseSalary: 112000, workload: 78, requires: { management: 66, business: 58 }, minExperience: 15 },
    ],
    stability: 42,
    meaning: 50,
    reputationLeverage: 0.24,
    pools: ['pool.career.physical', 'pool.career.shift'],
    tags: ['accessible', 'volatile', 'physical'],
  },
  {
    id: 'logistics',
    label: 'Operations & Logistics',
    blurb: 'The invisible machinery. Unglamorous, essential, promotable.',
    skillGrowth: { management: 3, technical: 1.6, financial: 1.4, communication: 1.2 },
    entry: { skills: {} },
    roles: [
      { level: 0, title: 'Coordinator', baseSalary: 38000, workload: 54, requires: {} },
      { level: 1, title: 'Operations Analyst', baseSalary: 58000, workload: 60, requires: { management: 30 }, minExperience: 2 },
      { level: 2, title: 'Operations Manager', baseSalary: 84000, workload: 68, requires: { management: 48, financial: 34 }, minExperience: 6 },
      { level: 3, title: 'Head of Operations', baseSalary: 118000, workload: 72, requires: { management: 62, financial: 48 }, minExperience: 11 },
      { level: 4, title: 'COO', baseSalary: 176000, workload: 80, requires: { management: 76, financial: 62, business: 62 }, minExperience: 17 },
    ],
    stability: 66,
    meaning: 48,
    reputationLeverage: 0.3,
    pools: ['pool.career.corporate', 'pool.career.stable'],
    tags: ['stable', 'promotable'],
  },
];

export const CAREER_FIELDS: Record<string, CareerField> = Object.fromEntries(
  FIELDS.map((f) => [f.id, f]),
);

export const CAREER_FIELD_LIST: readonly CareerField[] = FIELDS;
